import type { useToast } from "@chakra-ui/react";
import type { BudgetType } from "@common/interfaces";
import type { CreateCampaignFlowProps } from "../CreateCampaignFlow";
import actions from "src/actions";

export type LaunchCampaignParams = Parameters<
  CreateCampaignFlowProps["handleCreateCampaign"]
>[0];

export function buildLaunchCampaignPayload(params: LaunchCampaignParams) {
  const budgetCents = Math.round((params.budget?.amount?.amount ?? 0) * 100);
  const apiCreatives = params.creatives.map((c) => ({
    primary_text: c.primaryText.trim(),
    ...(c.squareMediaUrl ? { square_url: c.squareMediaUrl } : {}),
    ...(c.squareMediaFileKey ? { square_file_key: c.squareMediaFileKey } : {}),
    ...(c.squareMediaType ? { square_media_type: c.squareMediaType } : {}),
    ...(c.verticalMediaUrl ? { portrait_url: c.verticalMediaUrl } : {}),
    ...(c.verticalMediaFileKey
      ? { portrait_file_key: c.verticalMediaFileKey }
      : {}),
    ...(c.verticalMediaType
      ? { portrait_media_type: c.verticalMediaType }
      : {}),
    fb_page_id: c.fbPage?.id ?? "",
    fb_page_name: c.fbPage?.name ?? "",
    instagram_id: c.instagramAccount?.id ?? "",
    instagram_name: c.instagramAccount?.username ?? "",
  }));
  return {
    event_id: params.event_id,
    ad_account_id: params.ad_account_id,
    budget: {
      type: (params.budget?.type ?? "daily") as BudgetType,
      amount: budgetCents,
    },
    schedule: {
      start_at: new Date(params.schedule.start_at).toISOString(),
      end_at: new Date(params.schedule.end_at).toISOString(),
    },
    creatives: apiCreatives,
    targeting: {
      strategy: params.targeting?.strategy ?? "template",
      ...(params.targeting?.custom_audiences?.length
        ? { custom_audiences: params.targeting.custom_audiences }
        : {}),
    },
  };
}

export async function launchCampaignWithPolling(
  params: LaunchCampaignParams,
  deps: {
    toast: ReturnType<typeof useToast>;
    pollForResult: (args: {
      campaign_id: string;
      update_id: string;
      title: string;
      description: string;
      onStatusUpdate?: (status: string | null) => void;
      onDone?: () => void;
    }) => Promise<void>;
    onStatusUpdate?: (status: string | null) => void;
    onDone?: () => void;
  }
) {
  const payload = buildLaunchCampaignPayload(params);
  const response = (await actions.ADMIN.launchCampaign(payload)) as {
    campaign_id?: string;
    publish?: {
      attempted?: boolean;
      succeeded?: boolean;
      error?: string | null;
      update_id?: string | null;
    };
  };
  if (
    response?.publish?.attempted &&
    response?.campaign_id &&
    response?.publish?.update_id
  ) {
    await deps.pollForResult({
      campaign_id: response.campaign_id,
      update_id: response.publish.update_id,
      title: "Campaign published",
      description: "Campaign published",
      onStatusUpdate: deps.onStatusUpdate,
      onDone: deps.onDone,
    });
  }
}
